import React from 'react';
import { TextField } from '@material-ui/core';
import { DomicilioModel } from 'app/domicilio/models/DomicilioModel';
import { FieldProps, useField } from 'formik';
import { ComponentProps } from 'react';
import GooglePlacesInput, {
  domicilioGoogleInitialValues
} from './GooglePlacesInput';
import * as Yup from 'yup';

interface Props<T>
  extends FieldProps<T>,
    ComponentProps<typeof GooglePlacesInput> {
  className: string;
  size?: 'medium' | 'small';
}

function FormikAddress({
  field,
  className,
  size = 'medium',
  ...props
}: Props<DomicilioModel>) {
  const [_, meta] = useField(field.name);
  function handleChange(dom: Partial<DomicilioModel>) {
    props.form.setFieldValue(field.name, {
      ...field.value,
      ...dom
    });
    props.form.setFieldTouched(field.name, true);
  }

  const hasError = () => {
    return !!meta.error && meta.touched;
  };

  const getError = () => {
    if (hasError()) {
      if ((meta.error as any)?.calle) {
        return (meta.error as any)?.calle;
      }
      return 'La dirección no es válida';
    }
    return '';
  };

  return (
    <div className={className}>
      <div className={'flex space-x-4 '}>
        <GooglePlacesInput
          className="w-4/6"
          variant="outlined"
          value={{
            ...field.value,
            pais: '',
            codigoPostal: ''
          }}
          fullWidth
          label={props.label || 'Domicilio'}
          onChange={handleChange}
          error={hasError()}
          helperText={getError()}
          size={size}
        />
        <div className="w-1/6">
          <TextField
            variant="outlined"
            label="Piso"
            fullWidth
            value={field.value.piso}
            onChange={(e) => handleChange({ piso: e.target.value })}
            size={size}
          />
        </div>
        <div className="w-1/6">
          <TextField
            label="Dpto."
            variant="outlined"
            fullWidth
            value={field.value.departamento}
            onChange={(e) => handleChange({ departamento: e.target.value })}
            size={size}
          />
        </div>
      </div>
    </div>
  );
}

export const domicilioInitialValues: DomicilioModel = {
  ...domicilioGoogleInitialValues,
  piso: '',
  departamento: ''
};

export const domicilioValidation = Yup.object().shape({
  latitud: Yup.number(),
  longitud: Yup.number(),
  calle: Yup.string().required('Es necesario que elija una de las opciones')
});

export const domicilioValidationOptional = Yup.object().shape({
  latitud: Yup.number().nullable(),
  longitud: Yup.number().nullable(),
  calle: Yup.string().nullable(),
  numero: Yup.string().nullable(),
  partido: Yup.string().nullable(),
  localidad: Yup.string().nullable(),
  provincia: Yup.string().nullable(),
  departamento: Yup.string().nullable(),
  piso: Yup.string().nullable(),
  text: Yup.string().nullable()
}).test('address-validation', 'La dirección debe ser válida si se proporciona', function(value) {
  if (!value || (!value.calle && !value.text) || 
      (value.calle && value.calle.trim() === '' && value.text && value.text.trim() === '')) {
    return true; // Campo vacío es válido
  }
  // Si hay texto o calle, debe tener latitud y longitud válidas (no 0)
  if ((value.text && value.text.trim() !== '') || (value.calle && value.calle.trim() !== '')) {
    if (!value.latitud || !value.longitud || value.latitud === 0 || value.longitud === 0) {
      return this.createError({ message: 'Debe seleccionar una dirección válida del listado de Google Places' });
    }
  }
  return true;
});

export default FormikAddress;
